export type AIAction =
  | "rewrite"
  | "simplify"
  | "expand"
  | "fix"
  | "solve"
  | "prove"
  | "cite"
  | "outline"
  | "hint"
  | "proofread";

export type Discipline =
  | "math"
  | "cs"
  | "social_sciences"
  | "humanities"
  | "general";

export interface DocumentMetadata {
  title?: string;
  document_class?: string;
  packages?: string[];
}

// ---------------------------------------------------------------------------
// Provider / model types
// ---------------------------------------------------------------------------

export type ModelTier = "free" | "paid";

export interface ModelInfo {
  id: string;
  name: string;
  tier: ModelTier;
}

export interface ProviderInfo {
  id: string;
  name: string;
  models: ModelInfo[];
}

export interface AvailableModelsResponse {
  providers: ProviderInfo[];
  default_provider: string;
  default_model: string;
}

// ---------------------------------------------------------------------------
// Request / response types
// ---------------------------------------------------------------------------

interface AIRequestBase {
  provider?: string;
  model?: string;
}

export interface AIEditRequest extends AIRequestBase {
  text: string;
  action: AIAction;
  context?: string;
  doc_type: string;
  discipline?: Discipline;
  document_metadata?: DocumentMetadata;
}

export interface AIExplainRequest extends AIRequestBase {
  text: string;
  context?: string;
  discipline?: Discipline;
}

export interface AIConvertRequest extends AIRequestBase {
  text: string;
  from_format: string;
  to_format: string;
}

export interface AIErrorExplainRequest extends AIRequestBase {
  error_message: string;
  source_context?: string;
}

export interface AIResponse {
  result: string;
  explanation?: string;
  discipline?: string;
}

// Agent / Chat types

export type AgentStatus = "idle" | "thinking" | "reading" | "compiling" | "editing" | "done";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  thinking?: string;
  toolCalls?: ToolCallEvent[];
  toolResults?: ToolResultEvent[];
  edits?: EditEvent[];
  timestamp: number;
}

export interface ToolCallEvent {
  id: string;
  tool: string;
  params: Record<string, unknown>;
}

export interface ToolResultEvent {
  id: string;
  tool: string;
  success: boolean;
  output: string;
}

export interface EditEvent {
  type: string;
  toolCallId?: string;
  startLine?: number;
  endLine?: number;
  oldContent?: string;
  newContent?: string;
  content?: string;
  search?: string;
  replace?: string;
  full_new_content?: string;
}

/**
 * Normalize a raw edit event from the backend (snake_case) into the
 * frontend EditEvent shape (camelCase). For search_replace edits we
 * intentionally leave oldContent/newContent empty so the diff renderer
 * falls through to the smaller search/replace strings instead of
 * diffing the entire document.
 */
export function normalizeEditEvent(
  raw: Record<string, unknown>,
  toolCallId?: string,
): EditEvent {
  const type = (raw.type as string) || "";
  const isSearchReplace = type === "search_replace";
  return {
    type,
    toolCallId,
    startLine: (raw.start_line ?? raw.startLine) as number | undefined,
    endLine: (raw.end_line ?? raw.endLine) as number | undefined,
    oldContent: isSearchReplace
      ? undefined
      : ((raw.old_content ?? raw.oldContent) as string | undefined),
    newContent: isSearchReplace
      ? undefined
      : ((raw.new_content ?? raw.newContent) as string | undefined),
    search: raw.search as string | undefined,
    replace: raw.replace as string | undefined,
    content: raw.content as string | undefined,
    full_new_content: (raw.full_new_content as string) || undefined,
  };
}

export interface Conversation {
  id: string;
  documentId?: string;
  projectId?: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatRequest {
  conversationId?: string;
  documentId?: string;
  projectId?: string;
  message: string;
  cursorLine?: number;
  selectionStart?: number;
  selectionEnd?: number;
  selectedText?: string;
}
